import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCartIcon, HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { CartContext } from '../../context/CartContext';
import { getImageUrl, handleImageError } from '../../utils/imageHelper';
import toast from 'react-hot-toast';

const ProductCard = ({ product }) => {
  const { addToCart } = useContext(CartContext);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [imgError, setImgError] = useState(false);
  
  const totalStock = product.sizes?.reduce((sum, size) => sum + size.stock, 0) || product.stock_quantity || 0;
  const isOutOfStock = totalStock === 0;
  
  const discountPercentage = product.discount_price 
    ? Math.round(((product.original_price - product.discount_price) / product.original_price) * 100)
    : 0;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isOutOfStock) {
      toast.error('Out of stock');
      return;
    }
    
    const defaultSize = product.sizes?.[0]?.size || 'M';
    const defaultColor = product.colors?.[0]?.color || 'Black';
    
    const selectedSizeStock = product.sizes?.find(s => s.size === defaultSize)?.stock || 0;
    if (selectedSizeStock === 0) {
      toast.error(`Size ${defaultSize} unavailable`);
      return;
    }
    
    addToCart(product, defaultSize, defaultColor, 1);
    toast.success('Added to cart');
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
    toast.success(isWishlisted ? 'Removed' : 'Added to wishlist');
  };

  const imageUrl = getImageUrl(product.main_image);

  return (
    <Link to={`/products/${product.id}`} className="group block">
      <div className="relative">
        {/* Product Image Container */}
        <div className="relative overflow-hidden bg-gray-50 aspect-square">
          {!imgError ? (
            <img
              src={imageUrl}
              alt={product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition duration-500 ease-out"
              onError={(e) => {
                console.error('Image failed to load:', imageUrl);
                setImgError(true);
                handleImageError(e);
              }}
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <span className="text-gray-400 text-xs">No image</span>
            </div>
          )}
          
          {/* Badges - Minimal */}
          <div className="absolute top-3 left-3 flex flex-col gap-1">
            {discountPercentage > 0 && !isOutOfStock && (
              <span className="bg-gray-900 text-white text-[10px] font-medium px-2 py-0.5 tracking-wide">
                -{discountPercentage}%
              </span>
            )}
            {!isOutOfStock && totalStock <= 5 && totalStock > 0 && (
              <span className="bg-gray-500 text-white text-[10px] font-medium px-2 py-0.5 tracking-wide">
                Low stock
              </span>
            )}
          </div>
          
          {/* Out of Stock Overlay */}
          {isOutOfStock && (
            <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center">
              <span className="text-gray-400 text-xs tracking-wide uppercase border border-gray-300 px-3 py-1.5 bg-white">
                Sold out
              </span>
            </div>
          )}

          {/* Wishlist Button */}
          <button
            onClick={handleWishlist}
            className="absolute top-3 right-3 z-10 bg-white rounded-full p-1.5 shadow-sm hover:shadow transition"
          >
            {isWishlisted ? (
              <HeartSolidIcon className="h-4 w-4 text-gray-900" />
            ) : (
              <HeartIcon className="h-4 w-4 text-gray-400" />
            )}
          </button>
        </div>

        {/* Product Info - Minimal */}
        <div className="mt-4 space-y-1">
          <h3 className="text-sm font-light text-gray-900 truncate">
            {product.title}
          </h3>
          
          <p className="text-xs text-gray-400">
            {product.category?.name || 'Uncategorized'}
          </p>
          
          <div className="flex items-center gap-2 pt-1">
            {product.discount_price ? (
              <>
                <span className="text-sm font-medium text-gray-900">
                  ${parseFloat(product.discount_price).toFixed(2)}
                </span>
                <span className="text-xs text-gray-400 line-through">
                  ${parseFloat(product.original_price).toFixed(2)}
                </span>
              </>
            ) : (
              <span className="text-sm font-medium text-gray-900">
                ${parseFloat(product.original_price).toFixed(2)}
              </span>
            )}
          </div>

          {/* Add to Cart Button - Minimal */}
          <button
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className={`w-full mt-3 py-2.5 text-xs tracking-wide transition ${
              isOutOfStock 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
          >
            {isOutOfStock ? 'Sold Out' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;